import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Mail, Lock, ShieldAlert, Clock, ArrowLeft } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { useAuth } from '../../contexts/AuthContext'
import PhoenixLogo from '../../components/PhoenixLogo'

// Time-windowed sign-up: when current time is before this cutoff, the form
// allows new account creation. After the cutoff, the disabled-message banner
// shows instead. Adjust the timestamp manually to reopen / extend the window.
const SIGNUP_OPEN_UNTIL = new Date('2026-05-11T01:00:00.000Z')

function isSignUpOpen() {
  return Date.now() < SIGNUP_OPEN_UNTIL.getTime()
}

function formatRemaining(ms) {
  const totalMin = Math.max(0, Math.floor(ms / 60000))
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

export function LoginPage() {
  const { t, i18n } = useTranslation()
  const { signIn, signUp } = useAuth()
  const [mode, setMode] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showSignUpDisabledMsg, setShowSignUpDisabledMsg] = useState(false)
  const [now, setNow] = useState(Date.now())

  // Countdown ticker — only matters while sign-up is open
  useEffect(() => {
    if (!isSignUpOpen()) return undefined
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const remainingMs = SIGNUP_OPEN_UNTIL.getTime() - now
  const signUpOpen = remainingMs > 0
  const closesAt = SIGNUP_OPEN_UNTIL.toLocaleString(i18n.language, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)

    if (mode === 'signup') {
      if (!signUpOpen) {
        setError(t('phoenixTracker.auth.signUpDisabled'))
        setLoading(false)
        return
      }
      const { error: signUpErr } = await signUp(email, password)
      if (signUpErr) {
        setError(signUpErr.message)
      } else {
        setInfo(t('phoenixTracker.auth.confirmEmail'))
      }
    } else {
      const { error: authError } = await signIn(email, password)
      if (authError) setError(authError.message)
    }

    setLoading(false)
  }

  const switchToSignUp = () => {
    if (!signUpOpen) {
      setShowSignUpDisabledMsg(true)
      return
    }
    setMode('signup')
    setError(null)
    setInfo(null)
  }

  const switchToLogin = () => {
    setMode('login')
    setError(null)
    setInfo(null)
  }

  const isSignUp = mode === 'signup'

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <PhoenixLogo className="mx-auto w-20 h-20 mb-2" />
          <CardTitle>{t('phoenixTracker.title')}</CardTitle>
          <CardDescription>
            {isSignUp ? t('phoenixTracker.auth.signUp') : t('phoenixTracker.auth.signIn')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Sign-up open countdown — only when in login mode and sign-up is open */}
          {!isSignUp && signUpOpen && (
            <div className="mb-3 flex items-start gap-2 p-2.5 rounded-lg bg-app-primary-50 dark:bg-app-primary-900/20 border border-app-primary-200 dark:border-app-primary-800">
              <Clock className="w-3.5 h-3.5 text-app-primary-500 shrink-0 mt-0.5" />
              <div className="text-xs text-app-primary-700 dark:text-app-primary-300 leading-relaxed">
                <p>{t('phoenixTracker.auth.signUpOpenWindow', { remaining: formatRemaining(remainingMs) })}</p>
                <p className="opacity-80">{t('phoenixTracker.auth.signUpCloses', { when: closesAt })}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                {t('phoenixTracker.auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11 w-full ps-10 pe-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                {t('phoenixTracker.auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 w-full ps-10 pe-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
                  required
                  minLength={6}
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}

            {info && (
              <p className="text-sm text-green-600 dark:text-green-400 text-center">{info}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-app-primary-500 hover:bg-app-primary-600 text-white"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isSignUp ? (
                t('phoenixTracker.auth.signUp')
              ) : (
                t('phoenixTracker.auth.signIn')
              )}
            </Button>

            {/* Mode-switch links */}
            {isSignUp ? (
              <p className="text-sm text-center text-muted-foreground">
                {t('phoenixTracker.auth.hasAccount')}{' '}
                <button
                  type="button"
                  onClick={switchToLogin}
                  className="text-app-primary-500 hover:text-app-primary-600 font-medium inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5 rtl:-scale-x-100" />
                  {t('phoenixTracker.auth.signIn')}
                </button>
              </p>
            ) : showSignUpDisabledMsg && !signUpOpen ? (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <ShieldAlert className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {t('phoenixTracker.auth.signUpDisabled')}
                </p>
              </div>
            ) : (
              <p className="text-sm text-center text-muted-foreground">
                {t('phoenixTracker.auth.noAccount')}{' '}
                <button
                  type="button"
                  onClick={switchToSignUp}
                  className="text-app-primary-500 hover:text-app-primary-600 font-medium"
                >
                  {t('phoenixTracker.auth.signUp')}
                </button>
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
